"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { usePathname } from "next/navigation";
import { FaSpinner } from "react-icons/fa";
import { useNavData } from "./useNavData";
import DesktopNav from "./DesktopNav";
import MobileNav from "./MobileNav";
import SideDrawerMenu from "./SideDrawerMenu";

// Helper function to apply font only to numbers
const renderLabel = (text) => {
  if (typeof text !== 'string') return text;

  // Split the string by numbers (0-9)
  // This regex separates the text into an array: ["Text ", "123", " Text"]
  const parts = text.split(/([0-9]+)/);

  return parts.map((part, index) => {
    // Check if the part is a number
    if (/^[0-9]+$/.test(part)) {
      return (
        <span
          key={index}
          style={{ fontFamily: "'Roboto', sans-serif" }}
          className="font-extrabold"
          dir="ltr"
        >
          {part}
        </span>
      );
    }
    // Return regular text as is (using default Persian font)
    return part;
  });
};

// Function to recursively flatten all links into a searchable array
const flattenLinks = (links, parentLabel = '') => {
  const flattened = [];
  links.forEach(link => {
    // Add the link itself if it has an href and label
    if (link.href && link.label) {
      flattened.push({
        label: link.label,
        href: link.href,
        parentLabel: parentLabel
      });
    }

    // Recursively add all dropdown children
    if (link.dropdown && link.dropdown.length > 0) {
      const childrenFlattened = flattenLinks(link.dropdown, link.label);
      flattened.push(...childrenFlattened);
    }
  });
  return flattened;
};

export default function Navbar() {
  const pathname = usePathname();
  // Add any routes here that should use the transparent (Main Page) navbar style
  const transparentNavRoutes = ["/", "/warranty"];
  const isMainPage = transparentNavRoutes.includes(pathname);
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navbarRef = useRef(null);
  // --- Custom hook for data fetching ---
  const { navLinks, loadingProducts } = useNavData();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // --- NEW SEARCH STATES ---
  const [searchQuery, setSearchQuery] = useState("");
  const searchContainerRef = useRef(null); // Ref for the search component

  // Memoize the flattened links to avoid re-computation
  const allSearchableLinks = useMemo(() => {
    return navLinks.length > 0 ? flattenLinks(navLinks) : [];
  }, [navLinks]);

  // --- NEW: Search Filtering Effect ---
  const searchResults = useMemo(() => {
    if (searchQuery.trim() === "") {
      return [];
    }

    // Convert the search query to lowercase once for efficiency
    const lowerCaseQuery = searchQuery.toLowerCase();

    return allSearchableLinks.filter(link => {
      // Convert link label to lowercase for comparison
      const lowerCaseLabel = link.label.toLowerCase();

      // Convert parent label to lowercase (if it exists)
      const lowerCaseParentLabel = link.parentLabel ? link.parentLabel.toLowerCase() : '';

      // Search keywords in both the link's label and its parent's label
      return (
        lowerCaseLabel.includes(lowerCaseQuery) ||
        lowerCaseParentLabel.includes(lowerCaseQuery)
      );
    });
  }, [searchQuery, allSearchableLinks]);

  const toggleDropdown = useCallback((label, dropdown) => {
    // Check if the link has a dropdown
    if (dropdown?.length > 0) {
      setActiveDropdown((cur) => (cur === label ? null : label)); // Toggle dropdown
    } else {
      // For desktop, it navigates directly via <Link>
      // For mobile, we'll rely on the link itself
      setIsMenuOpen(false);
    }
  });

  const handleSearchLinkClick = useCallback(() => {
    setActiveDropdown(null);
    setIsMenuOpen(false);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      const target = event.target;

      // NEW: Close search results if click is outside the search container
      if (searchContainerRef.current && !searchContainerRef.current.contains(target)) {
        setSearchQuery("");
      }

      // Close when clicking ANY link or image
      if (target?.closest("a") || target?.closest("img")) {
        setActiveDropdown(null);
        setIsMenuOpen(false);
        return;
      }

      // Close when clicking OUTSIDE the navbar/header AND outside the drawer
      if (navbarRef.current && !navbarRef.current.contains(target)) {
        // Only close if it's not a click inside the side drawer panel itself
        if (!target?.closest('[role="dialog"]')) {
          setActiveDropdown(null);
          setIsMenuOpen(false);
        }
      }
    }

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [navbarRef, searchContainerRef]);


  if (loadingProducts) {
    // You can show a skeleton or loading indicator here while fetching products
    // For now, we'll render the header with no navigation links
    return (
      <header
        dir="rtl"
        className={`fixed top-0 left-0 w-full flex z-50 h-20 items-center justify-center transition-all duration-500 ease-in-out bg-white/95 backdrop-blur-md shadow-sm`}
      >
        <div className="flex items-center justify-between w-full px-4 navMD:px-8">
          {/* ... Mobile/Desktop structure placeholders ... */}
          <div className="text-[#313E75]"><FaSpinner className="animate-spin text-2xl text-left" /></div>
        </div>
      </header>
    );
  }

  // Determine if we should show the white navbar version
  const showWhiteNavbar = !isMainPage || activeDropdown;

  return (
    <>
      <header
        dir="rtl"
        ref={navbarRef}
        className={`font-extralight absolute top-0 left-0 w-full flex z-50 transition-all duration-500 ease-in-out ${showWhiteNavbar
          ? "bg-white/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.05)] py-2"
          : "pt-2 navMD:pt-[27px]"
          }`}
      >
        {/* Global Dark Backdrop (Only shows on main page when dropdown is closed) */}
        {!showWhiteNavbar && (
          <div className="absolute top-0 left-0 w-full h-[110px] navMD:h-[130px] bg-gradient-to-b from-black via-black/70 to-transparent -z-10 pointer-events-none" />
        )}

        <DesktopNav
          navLinks={navLinks}
          activeDropdown={activeDropdown}
          toggleDropdown={toggleDropdown}
          setActiveDropdown={setActiveDropdown}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          onLinkClick={handleSearchLinkClick}
          searchContainerRef={searchContainerRef}
          isScrolled={isScrolled}
          setIsMenuOpen={setIsMenuOpen}
          isWhiteNavbar={showWhiteNavbar}
        />
        <MobileNav
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          navLinks={navLinks}
          activeDropdown={activeDropdown}
          toggleDropdown={toggleDropdown}
          setActiveDropdown={setActiveDropdown}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          handleSearchLinkClick={handleSearchLinkClick}
          searchContainerRef={searchContainerRef}
          isScrolled={isScrolled}
          isWhiteNavbar={showWhiteNavbar}
        />
      </header>

      <SideDrawerMenu
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        navLinks={navLinks}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchResults={searchResults}
        handleSearchLinkClick={handleSearchLinkClick}
        searchContainerRef={searchContainerRef}
      />
    </>
  );
}